import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button, Modal, InputGroup } from 'react-bootstrap';
import { FaSearch, FaFilter, FaEraser, FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useConductoresSearch } from '../../hooks/useConductoresSearch';
import { useExtras } from '../../hooks/useExtras';
import useEmpresas from '../../hooks/useEmpresas';
import { getAllEstados } from '@apis/mantenedores/estados';

const AsignacionesFiltros = ({
    user,
    onFiltersChange,
    initialFilters = {},
    show,
    onHide
}) => {
    
    // Estados para filtros
    const [filtros, setFiltros] = useState({
        searchValue: initialFilters.searchValue || '',
        usuario_movil_id: initialFilters.usuario_movil_id || null,
        tipo_extra_id: initialFilters.tipo_extra_id || null,
        extra_id: initialFilters.extra_id || null,
        empresa_id: initialFilters.empresa_id || null,
        estado_id: initialFilters.estado_id || null,
        fecha_inicio: initialFilters.fecha_inicio || null,
        fecha_fin: initialFilters.fecha_fin || null
    });

    // Estados para dropdowns seleccionados
    const [selectedConductor, setSelectedConductor] = useState(null);
    const [selectedTipoExtra, setSelectedTipoExtra] = useState(null);
    const [selectedExtra, setSelectedExtra] = useState(null);
    const [selectedEmpresa, setSelectedEmpresa] = useState(null);
    const [selectedEstado, setSelectedEstado] = useState(null);
    const [estadosLista, setEstadosLista] = useState([]);
    
    // Estado para rango de fechas
    const [startDate, setStartDate] = useState(
        initialFilters.fecha_inicio ? new Date(initialFilters.fecha_inicio) : null
    );
    const [endDate, setEndDate] = useState(
        initialFilters.fecha_fin ? new Date(initialFilters.fecha_fin) : null
    );

    // Hooks para búsquedas
    const {
        conductores,
        loadingConductores,
        searchValue: conductorSearchValue,
        handleSearchChange: handleConductorSearch
    } = useConductoresSearch(user);

    const {
        tiposExtra,
        extras,
        setTipoExtraId
    } = useExtras(user, {});

    

    const {
        empresas,
        fetchEmpresas
    } = useEmpresas();

    // Cargar empresas al montar el componente
    useEffect(async () => {
        fetchEmpresas();
        
        try {
            const response = await getAllEstados(user, { tipo_estado: 'extra' });

            if (response.success) {
                setEstadosLista(response.data);
            } else {
                setError(response.error);
                setEstadosLista([]);
            }
        } catch (error) {
            console.error('Error al cargar estados:', error);
            setEstadosLista([]);
        }
    }, [fetchEmpresas]);

    // Preparar opciones para dropdowns
    const tipoExtraOptions = tiposExtra.map(tipo => ({
        value: tipo.id,
        label: tipo.tipo.toUpperCase(),
        data: tipo
    }));

    const extraOptions = extras.map(extra => ({
        value: extra.id,
        label: extra.extra,
        data: extra
    }));

    const empresaOptions = empresas.map(empresa => ({
        value: empresa.id,
        label: empresa.sigla,
        data: empresa
    }));

    const estadoOptions = estadosLista.flatMap(estadoGroup => 
        estadoGroup.estados.map(estado => ({
            value: estado.id,
            label: estado.estado,
            data: estado
        }))
    );

    // Handlers para cambios en filtros
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFiltros(prev => ({ ...prev, searchValue: value }));
    };

    const handleConductorChange = (selectedOption) => {
        setSelectedConductor(selectedOption);
        setFiltros(prev => ({ 
            ...prev, 
            usuario_movil_id: selectedOption?.value || null 
        }));
    };

    const handleTipoExtraChange = async (selectedOption) => {
        setSelectedTipoExtra(selectedOption);
        setSelectedExtra(null); // Limpiar extra seleccionado
        
        setFiltros(prev => ({ 
            ...prev, 
            tipo_extra_id: selectedOption?.value || null,
            extra_id: null // Limpiar extra_id cuando cambia tipo
        }));

        // Cargar extras del tipo seleccionado
        if (selectedOption?.value) {
            setTipoExtraId(selectedOption.value);
        } else {
            setTipoExtraId(null);
        }
    };

    const handleExtraChange = (selectedOption) => {
        setSelectedExtra(selectedOption);
        setFiltros(prev => ({ 
            ...prev, 
            extra_id: selectedOption?.value || null 
        }));
    };

    const handleEstadoChange = (selectedOption) => {
        setSelectedEstado(selectedOption);
        setFiltros(prev => ({
            ...prev,
            estado_id: selectedOption?.value || null
        }));
    };

    const handleEmpresaChange = (selectedOption) => {
        setSelectedEmpresa(selectedOption);
        setFiltros(prev => ({ 
            ...prev, 
            empresa_id: selectedOption?.value || null 
        }));
    };

    const handleFechaRangoChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        
        setFiltros(prev => ({ 
            ...prev, 
            fecha_inicio: start ? start.toISOString().split('T')[0] : null,
            fecha_fin: end ? end.toISOString().split('T')[0] : null
        }));
    };

    // Función para aplicar filtros
    const handleAplicarFiltros = () => {
        onFiltersChange(filtros);
        onHide(); // Cerrar modal
    };

    // Función para limpiar filtros
    const limpiarFiltros = () => {
        const filtrosLimpios = {
            searchValue: '',
            usuario_movil_id: null,
            tipo_extra_id: null,
            extra_id: null,
            empresa_id: null,
            estado_id: null,
            fecha_inicio: null,
            fecha_fin: null
        };
        
        setFiltros(filtrosLimpios);
        setSelectedConductor(null);
        setSelectedTipoExtra(null);
        setSelectedExtra(null);
        setSelectedEmpresa(null);
        setSelectedEstado(null);
        setStartDate(null);
        setEndDate(null);
        
        onFiltersChange(filtrosLimpios);
        onHide(); // Cerrar modal
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaFilter className="me-2" style={{ color: '#00b894' }} />
                    Filtros de Búsqueda
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">

                    {/* Conductor */}
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Conductor</Form.Label>
                            <Select
                                value={selectedConductor}
                                onChange={handleConductorChange}
                                options={conductores}
                                placeholder="Buscar conductor..."
                                isClearable
                                isLoading={loadingConductores}
                                isSearchable
                                noOptionsMessage={() => 
                                    conductorSearchValue.length < 2 
                                        ? 'Escriba al menos 2 caracteres'
                                        : 'No se encontraron conductores'
                                }
                                onInputChange={handleConductorSearch}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    {/* Tipo de Extra */}
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Tipo de Extra</Form.Label>
                            <Select
                                value={selectedTipoExtra}
                                onChange={handleTipoExtraChange}
                                options={tipoExtraOptions}
                                placeholder="Seleccionar tipo..."
                                isClearable
                                isSearchable
                                noOptionsMessage={() => 'No hay tipos disponibles'}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    {/* Extra */}
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Extra</Form.Label>
                            <Select
                                value={selectedExtra}
                                onChange={handleExtraChange}
                                options={extraOptions}
                                placeholder="Seleccionar extra..."
                                isClearable
                                isSearchable
                                isDisabled={!selectedTipoExtra}
                                noOptionsMessage={() => 
                                    !selectedTipoExtra 
                                        ? 'Primero seleccione un tipo de extra'
                                        : 'No hay extras disponibles'
                                }
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    {/* Empresa */}
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Empresa</Form.Label>
                            <Select
                                value={selectedEmpresa}
                                onChange={handleEmpresaChange}
                                options={empresaOptions}
                                placeholder="Seleccionar empresa..."
                                isClearable
                                isSearchable
                                noOptionsMessage={() => 'No hay empresas disponibles'}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>

                    {/* Rango de Fechas */}
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Rango de Fechas</Form.Label>
                            <DatePicker
                                selected={startDate}
                                onChange={handleFechaRangoChange}
                                startDate={startDate}
                                endDate={endDate}
                                selectsRange
                                placeholderText="Seleccionar rango de fechas o fecha desde..."
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                isClearable
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                maxDate={new Date()} // No permitir fechas futuras
                                monthsShown={2} // Mostrar 2 meses en el calendario
                                inline={false}
                                calendarClassName="rango-fechas-calendar"
                            />
                            {(startDate || endDate) && (
                                <Form.Text className="text-success mt-1">
                                    {startDate && !endDate && (
                                        <><strong>Desde:</strong> {startDate.toLocaleDateString('es-CL')} <em>(sin fecha límite)</em></>
                                    )}
                                    {startDate && endDate && (
                                        <><strong>Rango:</strong> {startDate.toLocaleDateString('es-CL')} hasta {endDate.toLocaleDateString('es-CL')}</>
                                    )}
                                </Form.Text>
                            )}
                            <Form.Text className="text-muted">
                                Puedes seleccionar una fecha de inicio sin fecha fin, o un rango completo
                            </Form.Text>
                        </Form.Group>
                    </Col>

                    {/* Estado */}
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Estado</Form.Label>
                            <Select
                                value={selectedEstado}
                                onChange={handleEstadoChange}
                                options={estadoOptions}
                                placeholder="Seleccionar estado..."
                                isClearable
                                isSearchable
                                noOptionsMessage={() => 'No hay estados disponibles'}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button 
                    variant="outline-secondary" 
                    onClick={limpiarFiltros}
                >
                    <FaEraser className="me-1" />
                    Limpiar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleAplicarFiltros}
                >
                    <FaFilter className="me-1" />
                    Aplicar Filtros
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

AsignacionesFiltros.propTypes = {
    user: PropTypes.object.isRequired,
    onFiltersChange: PropTypes.func.isRequired,
    initialFilters: PropTypes.object,
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired
};

export default AsignacionesFiltros;
